/**
 * useJobStream — SSE + polling fallback for console modals.
 *
 * Used by the 4 SSE modals: QbMaintenance, AptRunner, PackageJob, SignalInstall.
 * NOT used by DashboardUpdate, AppUpdate, or VpnJob (they have their own hooks).
 *
 * SSE dedup: On reconnect, the backend replays all existing lines via the `init`
 * event's `existingLines` count. This hook tracks how many lines were received
 * and skips batch-replayed lines on reconnect.
 */
import { useCallback, useEffect, useRef, useState } from "react";

type TerminalStatus = string;

type JobStreamConfig = {
  /** SSE stream URL (e.g. /api/admin/qb/stream) */
  streamUrl: string;
  /** Polling fallback URL (e.g. /api/admin/qb/status) */
  statusUrl: string;
  /** Polling interval in ms (default: 3000) */
  pollIntervalMs?: number;
  /** Extract job data from poll response (default: data.job) */
  extractJob?: (data: Record<string, unknown>) => Record<string, unknown> | null;
  /** Terminal status values (default: ["completed", "failed"]) */
  terminalStatuses?: string[];
};

type JobStreamState = {
  lines: string[];
  status: string;
  exitCode: number | null;
  durationMs: number | null;
  error: string | null;
};

type UseJobStreamReturn = JobStreamState & {
  connect: () => void;
  disconnect: () => void;
  reset: () => void;
  isTerminal: boolean;
};

const DEFAULT_POLL_MS = 3000;

export const useJobStream = (config: JobStreamConfig): UseJobStreamReturn => {
  const {
    streamUrl,
    statusUrl,
    pollIntervalMs = DEFAULT_POLL_MS,
    extractJob = (d) => (d.job as Record<string, unknown> | null),
    terminalStatuses = ["completed", "failed"],
  } = config;

  const [state, setState] = useState<JobStreamState>({
    lines: [],
    status: "idle",
    exitCode: null,
    durationMs: null,
    error: null,
  });

  const eventSourceRef = useRef<EventSource | null>(null);
  const pollTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const statusRef = useRef(state.status);
  const existingLinesCountRef = useRef(0);
  const receivedCountRef = useRef(0);
  statusRef.current = state.status;

  const isTerminal = terminalStatuses.includes(state.status);

  const stopPolling = useCallback(() => {
    if (pollTimerRef.current) {
      clearInterval(pollTimerRef.current);
      pollTimerRef.current = null;
    }
  }, []);

  const handleTerminal = useCallback((termStatus: string, code: number | null, duration: number | null) => {
    setState((prev) => ({
      ...prev,
      status: termStatus,
      exitCode: code,
      durationMs: duration,
    }));
    stopPolling();
  }, [stopPolling]);

  const startPollingFallback = useCallback(() => {
    if (pollTimerRef.current) return;

    const poll = async () => {
      if (terminalStatuses.includes(statusRef.current)) {
        stopPolling();
        return;
      }

      try {
        const res = await fetch(statusUrl, { credentials: "include", cache: "no-store" });
        if (!res.ok) return;
        const data = await res.json() as Record<string, unknown>;
        if (data.ok !== true) return;

        const job = extractJob(data);
        if (!job) {
          handleTerminal("failed", null, null);
          return;
        }

        const jobStatus = job.status as string;

        // Append any remaining lines we haven't seen
        const lastLines = job.lastLines as string[] | undefined;
        if (lastLines && lastLines.length > 0) {
          setState((prev) => {
            const existing = new Set(prev.lines.slice(-lastLines.length));
            const newLines = lastLines.filter((line) => !existing.has(line));
            return newLines.length > 0 ? { ...prev, lines: [...prev.lines, ...newLines] } : prev;
          });
        }

        if (terminalStatuses.includes(jobStatus)) {
          handleTerminal(
            jobStatus,
            typeof job.exitCode === "number" ? job.exitCode : null,
            typeof job.durationMs === "number" ? job.durationMs : null,
          );
        }
      } catch {
        // Non-fatal, retry next interval
      }
    };

    void poll();
    pollTimerRef.current = setInterval(() => void poll(), pollIntervalMs);
  }, [statusUrl, pollIntervalMs, extractJob, terminalStatuses, handleTerminal, stopPolling]);

  const disconnect = useCallback(() => {
    eventSourceRef.current?.close();
    eventSourceRef.current = null;
    stopPolling();
  }, [stopPolling]);

  const connect = useCallback(() => {
    disconnect();
    existingLinesCountRef.current = 0;
    receivedCountRef.current = 0;

    const es = new EventSource(streamUrl, { withCredentials: true });
    eventSourceRef.current = es;

    es.addEventListener("init", (event: MessageEvent) => {
      try {
        const parsed = JSON.parse(event.data as string) as { existingLines?: number };
        existingLinesCountRef.current = parsed.existingLines ?? 0;
        receivedCountRef.current = 0;
      } catch { /* ignore */ }
    });

    es.addEventListener("line", (event: MessageEvent) => {
      try {
        const parsed = JSON.parse(event.data as string) as { text: string };
        receivedCountRef.current += 1;

        // SSE dedup: skip batch-replayed lines we already have
        if (receivedCountRef.current <= existingLinesCountRef.current) {
          setState((prev) => {
            if (receivedCountRef.current <= prev.lines.length) return prev;
            return { ...prev, lines: [...prev.lines, parsed.text] };
          });
        } else {
          setState((prev) => ({ ...prev, lines: [...prev.lines, parsed.text] }));
        }
      } catch { /* ignore */ }
    });

    es.addEventListener("status", (event: MessageEvent) => {
      try {
        const parsed = JSON.parse(event.data as string) as {
          status: string;
          exitCode?: number | null;
          durationMs?: number | null;
        };
        if (terminalStatuses.includes(parsed.status)) {
          es.close();
          eventSourceRef.current = null;
          handleTerminal(parsed.status, parsed.exitCode ?? null, parsed.durationMs ?? null);
        } else {
          setState((prev) => ({ ...prev, status: parsed.status }));
        }
      } catch { /* ignore */ }
    });

    es.onerror = () => {
      es.close();
      eventSourceRef.current = null;
      if (!terminalStatuses.includes(statusRef.current)) {
        setState((prev) => ({ ...prev, error: "Stream connection lost. Polling for status..." }));
        startPollingFallback();
      }
    };
  }, [streamUrl, terminalStatuses, disconnect, handleTerminal, startPollingFallback]);

  const reset = useCallback(() => {
    disconnect();
    existingLinesCountRef.current = 0;
    receivedCountRef.current = 0;
    setState({ lines: [], status: "idle", exitCode: null, durationMs: null, error: null });
  }, [disconnect]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      eventSourceRef.current?.close();
      stopPolling();
    };
  }, [stopPolling]);

  return {
    ...state,
    connect,
    disconnect,
    reset,
    isTerminal,
  };
};
